<script lang="ts">
import { defineComponent, PropType, inject, ref, computed, watch, onMounted } from 'vue'
import { DOCUMENTS } from '@/_core/endpoint'
import apiRequestManager from '@/_core/apiRequestManager'
import ButtonSubmit from '@/components/button/ButtonSubmit.vue'
import solutionCategory from '@/models/solutionCategory'
import type { Ref } from 'vue'

export default defineComponent({
  name: 'DocumentsFilter',

  components: {
    ButtonSubmit,
  },

  props: {
    /**
     * 「課題・領域を選ぶ」の選択内容（サービスカテゴリ id）
     */
    inputSolutionCategoryIds: {
      type: Object as PropType<{
        name: string,
        value: number[],
      }>,
      required: true,
    },

    /**
     * 「資料の種類を選ぶ」の選択内容（資料種別 id）
     */
    inputDocumentCategoryIds: {
      type: Object as PropType<{
        name: string,
        value: string[],
      }>,
      required: true,
    },

    /**
     * 資料の検索結果の合計件数
     */
    totalCount: {
      type: Number,
      required: true,
    },

    form: {
      type: Object as PropType<Form>,
      required: true,
    },

    /**
     * ページネーションのクエリパラメータ key 名
     * @description 絞り込み遷移時にページネーション情報はリセットして遷移させる際に使用
     */
    pageQuery: {
      type: String,
      default: '',
    },
  },

  emits: ['update:selectedCategoryIds'],

  setup(props, { emit }) {
    // constants
    const SOLUTION_CATEGORIES = inject<Record<string, string>>('SOLUTION_CATEGORIES')
    const DOCUMENT_KIND_STATUS_TEXT = inject<Record<string, string>>('DOCUMENT_KIND_STATUS_TEXT')

    const { solutionCategoriesByDomain } = solutionCategory()
    const isShowModal = ref(false)
    const selectedCategoryIdsLocal: Ref<{
      solution: string[],
      document: string[],
    }> = ref({
      solution: [],
      document: [],
    })
    const totalCountLocal = ref(0)

    /**
     * カテゴリーセレクターのトリガー部分に表示する文字列
     * セレクター内で選択されたカテゴリーラベルがカンマ区切りで列挙される
     * ※ 未選択の場合は「すべて」が表示される
     */
    const selectedSolutionsLabel = computed<string>(() => {
      /**
       * 選択された「課題・領域」カテゴリーのラベル抽出
       */
      const solutionCategoryLabels = props.inputSolutionCategoryIds.value.map(id => SOLUTION_CATEGORIES![id as keyof typeof SOLUTION_CATEGORIES]).join(', ')

      /**
       * 選択された「資料の種類」カテゴリーのラベル抽出
       */
      const documentCategoryLabels = props.inputDocumentCategoryIds.value.length ? '種類：' + props.inputDocumentCategoryIds.value.map(id => DOCUMENT_KIND_STATUS_TEXT![id as keyof typeof DOCUMENT_KIND_STATUS_TEXT]).join(', ') : ''

      // セパレータ（「課題・領域」と「資料の種類」のどちらも入力されてる場合は、間を「 / 」で分割）
      const separator = solutionCategoryLabels && documentCategoryLabels ? ' / ' : ''

      // 連結文字列生成
      const label = `${solutionCategoryLabels}${separator}${documentCategoryLabels}`

      return label ? label : 'すべて'
    })

    const filteredURL = computed<string>(() => {
      const url = new URL(location.href)

      // ページネーションのクエリは削除（1 ページ目から表示）
      url.searchParams.delete(props.pageQuery)

      // 製品カテゴリのクエリパラメータ生成
      // 一旦カテゴリクエリをすべて消してから append し直す
      url.searchParams.delete(`${props.inputSolutionCategoryIds.name}[]`)
      if (selectedCategoryIdsLocal.value.solution.length) {
        // 配列の値をクエリパラメータとして渡す際の rails のルールに則り、
        // foo[]=bar&foo[]=baz という形でパラメータを生成する
        // URL searchParams の set の場合、key の重複が排除されるため、append で連結させる
        selectedCategoryIdsLocal.value.solution.forEach(v => {
          url.searchParams.append(`${props.inputSolutionCategoryIds.name}[]`, v)
        })
      }

      // 資料カテゴリのクエリパラメータ生成
      url.searchParams.delete(`${props.inputDocumentCategoryIds.name}[]`)
      if (selectedCategoryIdsLocal.value.document.length) {
        selectedCategoryIdsLocal.value.document.forEach(v => {
          url.searchParams.append(`${props.inputDocumentCategoryIds.name}[]`, v)
        })
      }

      return url.href
    })

    /**
     * URL クエリパラメータの「keyword」の値
     */
    const keywordValueOnQueryParam = computed<string>(() => {
      const KEYWORD_PARAM_NAME = 'keyword'
      const queryString = window.location.search
      const params = new URLSearchParams(queryString)
      const paramValue = params.get(KEYWORD_PARAM_NAME) || ''

      return paramValue
    })

    watch(() => selectedCategoryIdsLocal.value.solution, () => {
      fetchSelectedCategoryCount()
    })

    watch(() => selectedCategoryIdsLocal.value.document, () => {
      fetchSelectedCategoryCount()
    })

    onMounted(() => {
      init()
    })

    const init = (): void => {
      selectedCategoryIdsLocal.value.solution = props.inputSolutionCategoryIds.value.map(String)
      selectedCategoryIdsLocal.value.document = props.inputDocumentCategoryIds.value
      totalCountLocal.value = props.totalCount
    }

    const showModal = (): void => {
      init()
      isShowModal.value = true
    }

    const fetchSelectedCategoryCount = async () => {
      const params = {
        solution_category_ids: selectedCategoryIdsLocal.value.solution,
        document_kind_cds: selectedCategoryIdsLocal.value.document,
        keyword: keywordValueOnQueryParam.value,
      }

      try {
        await apiRequestManager.handleRequest({
          endpoint: DOCUMENTS.COUNT,
          params,
          callback: (response, isSuccess) => {
            if (isSuccess) {
              totalCountLocal.value = response.data.total_count
            }
          }
        })
      } catch (error) {
        console.log(error)
      }
    }

    const runFilter = () => {
      isShowModal.value = false
      emit(
        'update:selectedCategoryIds',
        selectedCategoryIdsLocal.value.solution.map(Number),
        selectedCategoryIdsLocal.value.document,
        filteredURL.value,
      )
    }

    const reset = (): void => {
      selectedCategoryIdsLocal.value.solution = []
      selectedCategoryIdsLocal.value.document = []
    }

    return {
      // constants
      DOCUMENT_KIND_STATUS_TEXT,

      // data
      selectedCategoryIdsLocal,
      totalCountLocal,
      isShowModal,

      // computed
      selectedSolutionsLabel,
      solutionCategoriesByDomain,
      filteredURL,

      // methods
      showModal,
      runFilter,
      reset,
    }
  }
})
</script>

<template>
  <div class="o-list__record">
    <dl class="o-list__record--unit">
      <dt class="o-list__record--term">
        カテゴリで探す
      </dt>

      <dd class="o-list__record--description">
        <div
          :class="[
            'o-action__categorySelector',
            { 'is-active': isShowModal },
          ]">
          <button
            class="o-action__categorySelector--item"
            @click="showModal">
            {{ selectedSolutionsLabel }}
          </button>
        <!-- /.o-action__categorySelector --></div>

        <ModalDefault
          v-model:is-show="isShowModal"
          title="資料をカテゴリから絞り込む"
          size="xxLarge"
          state-style-class="is-border">
          <template #description>
            <div class="o-heading__categorySelector">
              <h2 class="o-heading__categorySelector--item">
                課題・領域を選ぶ
              </h2>
            <!-- /.o-heading__categorySelector --></div>

            <div class="o-list__solutionDomain">
              <dl
                v-for="(solutionCategories, domainLabel) in solutionCategoriesByDomain"
                :key="domainLabel"
                class="o-list__solutionDomain--unit">
                <dt class="o-list__solutionDomain--term">
                  {{ domainLabel }}
                </dt>
                <dd class="o-list__solutionDomain--description">
                  <div class="o-list__solutionCategory">
                    <ul class="o-list__solutionCategory--items">
                      <li
                        v-for="(solutionCategory, key) in solutionCategories"
                        :key="key"
                        class="o-list__solutionCategory--item">
                        <div class="o-selector__solutionCategory">
                          <input
                            :id="`solution-category-${key}`"
                            v-model="selectedCategoryIdsLocal.solution"
                            class="o-selector__solutionCategory--item"
                            type="checkbox"
                            :name="inputSolutionCategoryIds.name"
                            :value="key">
                          <label
                            class="o-selector__solutionCategory--label"
                            :for="`solution-category-${key}`">
                            {{ solutionCategory }}
                          </label>
                        <!-- /.o-selector__solutionCategory --></div>
                      </li>
                    </ul>
                  <!-- /.o-list__solutionCategory --></div>
                </dd>
              </dl>
            <!-- /.o-list__solutionDomain --></div>

            <div class="o-heading__categorySelector">
              <h2 class="o-heading__categorySelector--item">
                資料の種類を選ぶ
              </h2>
            <!-- /.o-heading__categorySelector --></div>

            <div class="o-list__solutionCategory">
              <ul class="o-list__solutionCategory--items">
                <li
                  v-for="(documentCategory, categoryKey) in DOCUMENT_KIND_STATUS_TEXT"
                  :key="categoryKey"
                  class="o-list__solutionCategory--item">
                  <div class="o-selector__solutionCategory">
                    <input
                      :id="`solution-category-${categoryKey}`"
                      v-model="selectedCategoryIdsLocal.document"
                      class="o-selector__solutionCategory--item"
                      type="checkbox"
                      :name="inputDocumentCategoryIds.name"
                      :value="categoryKey">
                    <label
                      class="o-selector__solutionCategory--label"
                      :for="`solution-category-${categoryKey}`">
                      {{ documentCategory }}
                    </label>
                  <!-- /.o-selector__solutionCategory --></div>
                </li>
              </ul>
            <!-- /.o-list__solutionCategory --></div>
          </template>

          <template #action>
            <div class="l-cols__modalAction">
              <div class="l-cols__modalAction--col">
                <div class="o-action__reset">
                  <button
                    class="o-action__reset--item"
                    @click="reset">
                    リセット
                  </button>
                <!-- /.o-action__reset --></div>
              <!-- /.l-cols__modalAction col --></div>

              <div class="l-cols__modalAction--col">
                <ButtonSubmit
                  :is-link="false"
                  :href="filteredURL"
                  :is-font-small="true"
                  emit-name="filterDocuments"
                  @filter-documents="runFilter">
                  {{ totalCountLocal }}件の資料を表示する
                </ButtonSubmit>
              <!-- /.l-cols__modalAction col --></div>
            <!-- /.l-cols__modalAction --></div>
          </template>
        </ModalDefault>
      </dd>
    </dl>
  <!-- /.o-list__record --></div>
</template>

<style lang="sass" scoped>
.l-cols__modalAction
  $root: &
  display: flex
  align-items: center
  justify-content: center

  & &--col
    margin-left: 8px

    &:first-child
      margin-left: 0

  +breakpoint
    flex-direction: column

    #{$root}--col
      margin-left: 0

      &:first-child
        order: 1
        margin-top: 8px

// object
.o-heading__categorySelector
  margin: 2.2em 0 1.4em

  &:first-child
    margin-top: 0

  & &--item
    line-height: 1.6
    font-size: $font-large
    padding-left: 0.6em
    border-left: 4px solid $border-orange

.o-list__record
  // override CSS
  // doesn't work overflow as inner content of the flex-item
  // @see https://qiita.com/punkshiraishi/items/98f97d5db2f102026fb0
  & &--description
    min-width: 0

.o-list__solutionDomain
  & &--unit
    margin-top: 1em

    &:first-child
      margin-top: 0

  & &--term
    font-weight: bold

  & &--description
    margin-top: 0.5em

    &:first-child
      margin-top: 0

.o-list__solutionCategory
  & &--items
    display: flex
    flex-wrap: wrap
    margin:
      top: -0.5em
      left: -0.5em

  & &--item
    margin:
      top: 0.5em
      left: 0.5em

.o-action__categorySelector
  $root: &
  $icon-size: 4px
  $icon-margin: 16px

  & &--item
    width: 100%
    display: block
    position: relative
    overflow: hidden
    line-height: 2.5em
    text-align: left
    text-overflow: ellipsis
    padding-right: $icon-size * 2 + $icon-margin
    border-bottom: 1px solid $border-x-light-gray
    white-space: nowrap
    +transition

    &::after
      content: ""
      width: $icon-size
      height: $icon-size
      display: block
      position: absolute
      top: 50%
      right: 0
      transform: translateY(-50%) rotate(-45deg)
      margin:
        top: -2px
        right: 2px
      border:
        bottom: 2px solid $border-x-dark-gray
        left: 2px solid $border-x-dark-gray
      +transition

    +mouseover
      border-color: $border-orange

  &.is-active
    #{$root}--item
      &::after
        transform: translateY(-50%) rotate(135deg)
        margin:
          top: 0
          right: 2px

.o-action__reset
  & &--item
    width: 7.4em
    display: block
    line-height: 2.85em
    font-size: $font-small
    text-align: center
    border-radius: 100vh
    +transition

    +mouseover
      background-color: rgba($bg-xx-light-orange, 1)

.o-selector__solutionCategory
  $root: &
  height: 100%
  position: relative

  $font-size-px: 14
  $icon-width-px: 12
  $icon-height-px: 6
  $icon-margin-px: 8
  $horizontal-padding-px: 16
  $icon-width-em: math.div($icon-width-px, $font-size-px)
  $icon-height-em: math.div($icon-height-px, $font-size-px)
  $icon-margin-em: math.div($icon-margin-px, $font-size-px)
  $horizontal-padding-em: math.div($horizontal-padding-px, $font-size-px)
  & &--label
    height: 100%
    position: relative
    display: block
    overflow: hidden
    line-height: 1.8
    font-size: $font-small
    text-align: center
    padding: 0.4em #{$horizontal-padding-em}em 0.4em #{$horizontal-padding-em + $icon-width-em + $icon-margin-em}em
    background-color: rgba($bg-white, 1)
    border: 1px solid $border-x-light-gray
    border-radius: 100vh
    +transition

    // チェックアイコン
    &::before
      content: ""
      width: #{math.div($icon-width-em, 1.5)}em
      height: #{math.div($icon-height-em, 1.5)}em
      display: block
      position: absolute
      top: 50%
      left: #{$horizontal-padding-em}em
      transform: translateY(-50%) rotate(-45deg)
      margin-top: -0.1em
      border-bottom: 2px solid $border-x-light-gray
      border-left: 2px solid $border-x-light-gray
      +transition

    +mouseover
      border-color: $border-orange

  // input[type="checkbox"] 要素
  // HTML のチェックボックス・ラジオボタン系はスタイリングできないため、非表示（width, height 0）
  & &--item
    width: 0
    height: 0
    position: absolute
    top: 0
    left: 0

    // 選択状態
    &:checked
      + #{$root}--label
        background-color: rgba($bg-xx-light-orange, 1)

        &::before
          border-bottom-color: $border-orange
          border-left-color: $border-orange

    // 非活性状態
    &:disabled
      + #{$root}--label
        background-color: rgba($bg-gray, 1)

      // 非活性且つ選択状態
      &:checked
        #{$root}--label
          color: $font-gray
          background-color: rgba($bg-orange, 1)
</style>
